import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { PosHit, PosLookupKind } from '../api';
import { formatCents, lineKey } from './money';
import { COLORS } from './theme';

/** Why a row matched. Shown as a chip so the cashier can tell a scanned
 *  barcode hit from a fuzzy name guess at a glance. */
const KIND_LABELS: Record<Exclude<PosLookupKind, 'none'>, string> = {
  sku: 'SKU',
  upc: 'UPC',
  name: 'name',
};

type Props = {
  query: string;
  onQueryChange: (q: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  kind: PosLookupKind;
  hits: PosHit[];
  loading: boolean;
  error: string;
  onPick: (hit: PosHit) => void;
};

const SearchPanel: React.FC<Props> = ({
  query,
  onQueryChange,
  onKeyDown,
  inputRef,
  kind,
  hits,
  loading,
  error,
  onPick,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      // Top padding comes from the mode-pill row above.
      px: 2,
      pb: 2,
      gap: 2,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        inputRef={inputRef}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Scan barcode or type item name…"
        autoComplete="off"
        // The scanner is a keyboard. Autocorrect/autocapitalize on a
        // tablet would mangle a scanned code before it ever reaches us.
        inputProps={{
          autoCapitalize: 'off',
          autoCorrect: 'off',
          spellCheck: false,
          'aria-label': 'Item search',
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: COLORS.textMuted }} />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={18} />
            </InputAdornment>
          ) : null,
          sx: {
            fontSize: '1.35rem',
            fontWeight: 600,
            backgroundColor: COLORS.surface,
            py: 0.5,
          },
        }}
      />
      {/* Thin progress line instead of a blocking spinner — the cashier
          can keep typing while a lookup is in flight. */}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        />
      )}
    </Box>

    {error && <Alert severity="error">{error}</Alert>}

    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        backgroundColor: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
      }}
    >
      {hits.length === 0 ? (
        <EmptyState query={query} kind={kind} loading={loading} />
      ) : (
        <List disablePadding>
          {hits.map((hit) => (
            <ResultRow
              key={lineKey(hit)}
              hit={hit}
              kind={kind}
              onPick={onPick}
            />
          ))}
        </List>
      )}
    </Box>
  </Box>
);

const EmptyState: React.FC<{
  query: string;
  kind: PosLookupKind;
  loading: boolean;
}> = ({ query, kind, loading }) => {
  const noMatch = !loading && query.trim().length >= 2 && kind === 'none';
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        color: COLORS.textMuted,
        p: 4,
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {noMatch ? 'No matches' : 'Scan or search to begin'}
      </Typography>
      <Typography variant="body2">
        {noMatch
          ? `Nothing in the catalog for “${query.trim()}”.`
          : 'Barcode, SKU, or product name.'}
      </Typography>
    </Box>
  );
};

const ResultRow: React.FC<{
  hit: PosHit;
  kind: PosLookupKind;
  onPick: (hit: PosHit) => void;
}> = ({ hit, kind, onPick }) => {
  const noPrice = hit.price == null;
  return (
    <ListItemButton
      onClick={() => onPick(hit)}
      disabled={noPrice}
      sx={{
        minHeight: 72,
        borderBottom: `1px solid ${COLORS.border}`,
        gap: 2,
        px: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '1.05rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {hit.brand ? `${hit.brand} — ` : ''}
          {hit.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {kind !== 'none' && (
            <Chip
              label={KIND_LABELS[kind]}
              size="small"
              color={kind === 'name' ? 'default' : 'success'}
              variant={kind === 'name' ? 'outlined' : 'filled'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {/* Which shop. Results span the grow shop, back room, and
              front room, so this is load-bearing, not decoration. */}
          <Chip
            label={hit.tenant_label}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          <Typography
            variant="body2"
            sx={{ color: COLORS.textMuted, fontFamily: 'monospace' }}
          >
            {hit.sku}
          </Typography>
          <StockLabel stock={hit.stock} />
        </Box>
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', whiteSpace: 'nowrap' }}>
        {noPrice ? 'no price' : formatCents(hit.price as number)}
      </Typography>
    </ListItemButton>
  );
};

/** Stock is advisory. Zero gets a warning colour but the row stays
 *  tappable — the cashier holding the item is more authoritative than
 *  the inventory table. */
const StockLabel: React.FC<{ stock: number }> = ({ stock }) => (
  <Typography
    variant="body2"
    sx={{
      color: stock <= 0 ? COLORS.warnText : COLORS.textMuted,
      fontWeight: stock <= 0 ? 700 : 400,
    }}
  >
    {stock <= 0 ? 'stock 0 ⚠' : `stock ${stock}`}
  </Typography>
);

export default SearchPanel;
